import { useTranslation } from "react-i18next";
import { Address } from "../../redux/apiSpecifications/apiSubjects";
import { FormRow } from "../../style/form";
import { InputTextStyled, SelectStyled } from "../../style/Input";
import { RowSpacer } from "../../style/containers";
import { useEffect, useMemo, useState } from "react";
import { toFullAddress } from "../../utils/address";
import SelectCountry from "../Inputs/SelectCountry";
import { checkPostalCode, isNotEmpty } from "../../validation/common";
import {
  AddressFormData,
  useLazyGetAddressFormDataQuery,
} from "../../redux/apiSpecifications/apiFesf";
import { SelectPair } from "../../types/common.types";
import styled from "styled-components";
import { useAzureMaps } from "../../hooks/useAzureMaps";
import FormModal from "../Modals/FormModal";

const StreetWrapper = styled.div`
  display: grid;
  grid-template-columns: 4fr 1fr;
  gap: 2em;
  margin-bottom: 1em;

  .ant-form-item-row {
    flex-direction: column;

    .ant-form-item-label {
      text-align: left;
    }
  }
`;

interface IAddressFormProps {
  address: Address | undefined;
  options?: { civicMandatory: boolean };
  onChange?: (newAddress: Address) => void;
}

const AddressForm = ({ address, options, onChange }: IAddressFormProps) => {
  const { t } = useTranslation();

  const [getAddresses] = useLazyGetAddressFormDataQuery();

  const [nationalAddresses, setNationalAddresses] = useState<
    AddressFormData | undefined
  >(undefined);

  useEffect(() => {
    if (address?.country === undefined) setNationalAddresses(undefined);
    else if (
      address?.province !== undefined &&
      nationalAddresses?.provinces?.length === 0
    )
      return;
    else if (
      address?.city !== undefined &&
      nationalAddresses?.cities?.length === 0
    )
      return;
    else {
      getAddresses({
        country: address?.country,
        province: address?.province,
        city: address?.city,
      })
        .unwrap()
        .then((result) => {
          setNationalAddresses({
            provinces: result.provinces,
            cities: result.cities,
            postalCodes: result.postalCodes,
          });
        })
        .catch((err) => {
          console.error("Errore durante il recupero degli indirizzi:", err);
        });
    }
  }, [address?.country, address?.province, address?.city]);

  const handleOnChange = (value: any, field: string) => {
    const _value = value === "" ? undefined : value;

    if (onChange) {
      if (field === "country") {
        const newAddress = {
          ...address,
          country: _value,
          province: undefined,
          city: undefined,
          postalCode: undefined,
        } as Address;

        onChange(newAddress);
      } else if (field === "province") {
        onChange({
          ...address,
          province: _value,
          city:
            nationalAddresses?.provinces?.length === 0
              ? address?.city
              : undefined,
          postalCode:
            nationalAddresses?.provinces?.length === 0
              ? address?.postalCode
              : undefined,
        });
      } else if (field === "city") {
        onChange({
          ...address,
          city: _value,
          postalCode:
            nationalAddresses?.cities?.length === 0
              ? address?.postalCode
              : undefined,
        });
      } else {
        onChange({
          ...address,
          [field]: _value,
        });
      }
    }

    // if (onChange) onChange(value === "" ? undefined : value, field);
  };

  const provinceOptions = useMemo(() => {
    if (
      nationalAddresses?.provinces === undefined ||
      nationalAddresses?.provinces.length === 0
    )
      return [];

    return nationalAddresses?.provinces
      .map(
        (province) =>
          ({
            value: province.districtCode,
            label: province.districtName,
          } as SelectPair)
      )
      .sort((p1, p2) =>
        p1.label > p2.label ? 1 : p1.label < p2.label ? -1 : 0
      );
  }, [nationalAddresses?.provinces]);

  const citiesOptions = useMemo(() => {
    if (
      nationalAddresses?.cities === undefined ||
      nationalAddresses?.cities.length === 0
    )
      return [];

    return nationalAddresses?.cities
      .map(
        (city) =>
          ({
            value: city,
            label: city,
          } as SelectPair)
      )
      .sort((p1, p2) =>
        p1.label > p2.label ? 1 : p1.label < p2.label ? -1 : 0
      );
  }, [nationalAddresses?.cities]);

  const postalCodeOptions = useMemo(() => {
    if (
      nationalAddresses?.postalCodes === undefined ||
      nationalAddresses?.postalCodes.length === 0
    )
      return [];

    return nationalAddresses?.postalCodes
      .map(
        (postalCode) =>
          ({
            value: postalCode,
            label: postalCode,
          } as SelectPair)
      )
      .sort((p1, p2) =>
        p1.label > p2.label ? 1 : p1.label < p2.label ? -1 : 0
      );
  }, [nationalAddresses?.postalCodes]);

  const enableProvice = address?.country !== undefined;
  const enableCity =
    enableProvice && nationalAddresses?.provinces?.length === 0;
  const enablePostalCode =
    enableCity && nationalAddresses?.cities?.length === 0;

  return (
    <div>
      <FormRow>
        <SelectCountry
          onChange={(country: string) => handleOnChange(country, "country")}
          value={address?.country}
        />

        <RowSpacer />

        {address?.country === undefined ||
        nationalAddresses === undefined ||
        nationalAddresses.provinces?.length === 0 ? (
          <InputTextStyled
            label={t("select-province")}
            tooltip={t("select-province-tooltip")}
            placeholder={t("address-province-placeholder")}
            onChange={(province: string) =>
              handleOnChange(province, "province")
            }
            value={address?.province}
            required={true}
            disabled={!enableProvice}
          />
        ) : (
          <SelectStyled
            label={t("select-province")}
            tooltip={t("select-province-tooltip")}
            onChange={(province: string) =>
              handleOnChange(province, "province")
            }
            value={address?.province}
            options={provinceOptions}
            showSearch
            filterOption={(input: string, option: any) => {
              const text = input.toLowerCase();
              return option.label.toLowerCase().includes(text) || false;
            }}
            required={true}
          />
        )}
      </FormRow>

      <FormRow>
        {address?.province === undefined ||
        nationalAddresses === undefined ||
        nationalAddresses.cities?.length === 0 ? (
          <InputTextStyled
            label={t("address-city-label")}
            tooltip={t("address-city-tooltip")}
            placeholder={t("address-city-placeholder")}
            onChange={(city: string) => handleOnChange(city, "city")}
            value={address?.city}
            required={true}
            disabled={!enableCity}
          />
        ) : (
          <SelectStyled
            label={t("address-city-label")}
            tooltip={t("address-city-tooltip")}
            onChange={(city: string) => handleOnChange(city, "city")}
            value={address?.city}
            options={citiesOptions}
            required={true}
            showSearch
            filterOption={(input: string, option: any) => {
              const text = input.toLowerCase();
              return option.label.toLowerCase().includes(text) || false;
            }}
          />
        )}

        <RowSpacer />

        {address?.city === undefined ||
        nationalAddresses === undefined ||
        nationalAddresses.postalCodes?.length === 0 ? (
          <InputTextStyled
            label={t("address-postalcode-label")}
            tooltip={t("address-postalcode-tooltip")}
            placeholder={t("address-postalcode-placeholder")}
            onChange={(postalCode: string) =>
              handleOnChange(postalCode, "postalCode")
            }
            value={address?.postalCode}
            required={true}
            disabled={!enablePostalCode}
          />
        ) : (
          <SelectStyled
            label={t("address-postalcode-label")}
            tooltip={t("address-postalcode-tooltip")}
            onChange={(postalCode: string) =>
              handleOnChange(postalCode, "postalCode")
            }
            value={address?.postalCode}
            options={postalCodeOptions}
            required={true}
            showSearch
            filterOption={(input: string, option: any) => {
              const text = input.toLowerCase();
              return option.label.toLowerCase().includes(text) || false;
            }}
          />
        )}
      </FormRow>

      <StreetWrapper>
        <InputTextStyled
          label={t("address-street-label")}
          tooltip={t("address-street-tooltip")}
          placeholder={t("address-street-placeholder")}
          onChange={(street: string) => {
            handleOnChange(street, "street");
          }}
          value={address?.street}
          required={true}
        />

        <InputTextStyled
          label={t("address-civic-label")}
          tooltip={t("address-civic-tooltip")}
          placeholder={t("address-civic-placeholder")}
          onChange={(civic: string) => handleOnChange(civic, "civic")}
          value={address?.civic}
          required={options?.civicMandatory}
        />
      </StreetWrapper>
    </div>
  );
};

const AddressModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3em;
`;

interface IAddressModalProps {
  open: boolean;
  address: Address | undefined;
  options?: { civicMandatory?: boolean; enableGeocodePoints?: boolean };
  onCancel: () => void;
  onConfirm: (address: Address) => void;
}

export const AddressModal = ({
  open,
  address,
  options,
  onCancel,
  onConfirm,
}: IAddressModalProps) => {
  const [_address, setAddress] = useState<Address | undefined>(undefined);

  const { getGeocodePromise } = useAzureMaps();

  const _civicMandatory: boolean = options?.civicMandatory ?? true;
  const _enableGeocodePoints: boolean = options?.enableGeocodePoints ?? false;

  useEffect(() => {
    if (open) {
      setAddress(address);
    }
  }, [open]);

  const onChangeAddress = (newAddress: Address) => {
    setAddress(newAddress);
  };

  const handleOnConfirmAddress = async () => {
    if (!_address) return;

    if (_enableGeocodePoints) {
      const {
        street,
        civic,
        country: countryRegion,
        province: adminDistrict,
        city: locality,
        postalCode,
      } = _address;

      const addressLine = [street, civic].filter(Boolean).join(" ");

      getGeocodePromise(
        addressLine,
        countryRegion,
        adminDistrict, //!.toLowerCase(),
        locality,
        postalCode
      )
        .then((geocodePoints) =>
          onConfirm({
            ..._address,
            latitude: geocodePoints?.latitude,
            longitude: geocodePoints?.longitude,
          })
        )
        .catch((err) => {
          onConfirm({
            ..._address,
            latitude: undefined,
            longitude: undefined,
          });
        });
    } else {
      onConfirm({
        ..._address,
        latitude: undefined,
        longitude: undefined,
      });
    }
  };

  const isSubjectAddressValid = useMemo(() => {
    if (_address === undefined) return false;

    const { street, civic, city, postalCode, province, country } = _address;

    return (
      isNotEmpty(street) &&
      (_civicMandatory === true ? isNotEmpty(civic) : true) &&
      isNotEmpty(city) &&
      checkPostalCode(postalCode?.toString() || "") &&
      isNotEmpty(province) &&
      isNotEmpty(country)
    );
  }, [_address, options]);

  return (
    <FormModal
      open={open}
      width={700}
      isConfirmButtonDisabled={!isSubjectAddressValid}
      onCancel={onCancel}
      onConfirm={handleOnConfirmAddress}
    >
      {_address && (
        <AddressModalWrapper>
          <AddressForm
            address={_address}
            options={{ civicMandatory: _civicMandatory }}
            onChange={onChangeAddress}
          />
        </AddressModalWrapper>
      )}
    </FormModal>
  );
};

interface IAddressTextProps {
  address?: Address | undefined;
  label?: string | null;
  placeholder?: string | null;
  required?: boolean;
  readOnly?: boolean;
  classNameInput?: string;
  options?: { civicMandatory?: boolean; enableGeocodePoints?: boolean };
  onChange: (address: Address) => void;
}

const AddressText = ({
  address,
  readOnly = false,
  options,
  onChange,
  ...rest
}: IAddressTextProps) => {
  const [_address, setAddress] = useState<Address | undefined>(undefined);

  return (
    <>
      <InputTextStyled
        value={toFullAddress(address)}
        readOnly={true}
        styleInput={{ cursor: "pointer" }}
        disabled={readOnly}
        {...rest}
        onClick={() => setAddress(address || {})}
      />

      {_address && (
        <AddressModal
          open={true}
          address={_address}
          onCancel={() => setAddress(undefined)}
          options={options}
          onConfirm={(address) => {
            setAddress(undefined);

            onChange(address);
          }}
        />
      )}
    </>
  );
};

export default AddressText;
